import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Building2, FolderKanban } from 'lucide-react';
import { clientAPI } from '../api';

export default function ClientsPage() {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      clientAPI.list({ ...(search ? { search } : {}), ...(type ? { client_type: type } : {}) })
        .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search, type]);

  return (
    <div className="space-y-4" data-testid="clients-container">
      <div>
        <h1 className="text-4xl font-outfit font-semibold sm:text-5xl">Clients</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every client and the independent projects that belong to them</p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="clients-search"
            placeholder="Search name, company or mobile"
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-900" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="clients-filter-type"
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
          <option value="">B2C + B2B</option><option value="b2c">B2C</option><option value="b2b">B2B</option>
        </select>
      </div>

      {loading ? <div className="clay-card p-6 text-sm text-muted-foreground" data-testid="clients-loading">Loading clients…</div>
        : rows.length === 0 ? (
          <div className="clay-card p-8 text-center text-sm text-muted-foreground" data-testid="clients-empty">
            No clients yet. Clients are a separate register from leads — a client record appears once you add a project for one (converted leads alone do not create one).
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="clients-list">
            {rows.map((c) => (
              <Link key={c.client_id} to={`/clients/${c.client_id}`} data-testid={`client-card-${c.client_id}`}
                className="clay-card p-4 transition-transform hover:-translate-y-0.5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    {c.company_name && <p className="text-xs text-muted-foreground">{c.company_name}</p>}
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    c.client_type === 'b2b' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30' : 'bg-slate-100 text-slate-600 dark:bg-slate-800'}`}>
                    {c.client_type === 'b2b' ? <Building2 className="h-3 w-3" /> : <Users className="h-3 w-3" />}
                    {(c.client_type || 'b2c').toUpperCase()}
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{c.primary_mobile || '—'}{c.city ? ` · ${c.city}` : ''}</p>
                <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                  <FolderKanban className="h-3.5 w-3.5" /> {c.project_count ?? 0} project{(c.project_count ?? 0) === 1 ? '' : 's'}
                </p>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}
